// Mind › Words into maps (ba-web, on ba-mind's MIND-003 model): NeuroQuery's pretrained text-to-map model, run in this
// browser by ba-mind's encoder (pipelines/mind/text/encoder.mjs), its map kept on the model's own 46 × 55 × 46 grid at
// 4 mm. Nothing goes onto cortical vertices: the source template's lineage is unresolved (header.projection), so the
// view draws three native slices itself (features/Text.tsx) and the scene's hemispheres stand hidden. The model's six
// files load once from sims/mind/neuroquery-native-v1/ by directory URL (no index row, the adapter's own hash checks);
// encode() runs here and asks the network for nothing, so what is typed never leaves the page.

import { create } from 'zustand';
import { defaultSource } from '../data/sims/files.ts';
import { loadTextEncoder, type TextEncoder, type TextModelHeader } from '../../../../pipelines/mind/text/encoder.mjs';
import { changeColour } from './changeScale.ts';

export const TEXT_RUN = 'mind/neuroquery-native-v1';
/** The topic the view opens on, and the one the bar leaves out (app/url.ts `q=`). */
export const DEFAULT_QUERY = 'working memory';
/** The display scale, ±8 standardized score: a visual choice, not a threshold (ba-mind's fixture uses the same). */
export const SCALE = 8;
/** Example topics, each a phrase the model's vocabulary holds (pipelines/mind/text/queries.json). */
export const EXAMPLES: { label: string; q: string }[] = [
  { label: 'Vision', q: 'vision' },
  { label: 'Language', q: 'language comprehension' },
  { label: 'Memory', q: 'working memory' },
  { label: 'Pain', q: 'pain' },
  { label: 'Music', q: 'music' },
  { label: 'Emotion', q: 'emotion' },
  { label: 'Faces', q: 'face perception' },
  { label: 'Motor', q: 'left hand movement' },
];
/** The longest topic the box and the bar take. */
export const MAX_QUERY = 200;

export const TEXT_CAPTION = 'NeuroQuery text-to-map · fsaverage5 cortex · 4 mm slices';
export const TEXT_METHODS =
  'Words into maps runs NeuroQuery’s pretrained model (2020-02-13) in this browser: for a topic, the standardized score at each voxel predicts how strongly the neuroimaging literature on that topic reports it, a prediction over published coordinates, not measured activation and not a p-value. The text is tokenised with the model’s own 6,308 terms and 7,547 phrases and it reads terms, not sentences: its stop words are dropped and word order is ignored, so negation isn’t read and “not pain” maps exactly as “pain”; the typed words it didn’t use are listed beside the recognised terms. The slices stay on the model’s own 46 × 55 × 46 grid at 4 mm (28,542 voxels; ba-mind’s audit identified the mask’s reference grid as MNI152NLin6Asym, MIND-003A). On the cortex the map is ba-mind’s MIND-003C projection (neuroquery-cortex-v1): the same model carried to fsaverage5’s 20,484 vertices, 18,474 of them supported; the rest, the medial wall and the subcortex stay bare. That projection passed a conditional fidelity test only: on the pre-existing brain support the browser model matches the reference projection to an aggregate RMSE of 9.9 × 10⁻⁸ over 32 queries (a zero map scores 2.32); the whole-head template-identity test (MIND-003B) failed, so nothing is claimed beyond that support, and no biological accuracy is claimed: a 4 mm source, no subcortex or cerebellum, negation unread, literature peaks registered heterogeneously. ba-mind’s local encoder reproduces the Python model on 24 queries (aggregate RMSE 8.3 × 10⁻⁸, MIND-003). The model loads once (26.9 MB); typing asks the network for nothing.';

export type Axis = 0 | 1 | 2;
export type Shape = [number, number, number];
export type Ijk = [number, number, number];

/** The three slices as shown, in the fixture's order: through the grid's k, j and i axes. */
export const SLICES: { axis: Axis; name: string; letter: 'x' | 'y' | 'z'; dirs: [string, string, string, string] }[] = [
  // dirs: the letters at the left, right, top and bottom edges of the picture.
  { axis: 2, name: 'Axial', letter: 'z', dirs: ['L', 'R', 'A', 'P'] },
  { axis: 1, name: 'Coronal', letter: 'y', dirs: ['L', 'R', 'S', 'I'] },
  { axis: 0, name: 'Sagittal', letter: 'x', dirs: ['A', 'P', 'S', 'I'] },
];

export interface Grid {
  shape: Shape;
  affine: number[][];
}

export interface Peak {
  value: number;
  ijk: Ijk;
  /** RAS+ mm in the model's own space, from the header's affine. */
  mm: Ijk;
}

export const flatIndex = (shape: Shape, i: number, j: number, k: number): number => (i * shape[1] + j) * shape[2] + k;

export const ijkOf = (shape: Shape, flat: number): Ijk => [Math.floor(flat / (shape[1] * shape[2])), Math.floor(flat / shape[2]) % shape[1], flat % shape[2]];

/** Voxel centre in RAS+ mm (the grid is axis-aligned: the header's affine has one scale and one offset per axis). */
export const mmOf = (affine: number[][], ijk: Ijk): Ijk => [0, 1, 2].map((a) => Math.round(affine[a]![a]! * ijk[a as Axis] + affine[a]![3]!)) as Ijk;

/** The map scattered onto the dense grid, NaN where the mask has no voxel. */
export function denseVolume(map: Float32Array, indices: Uint32Array, shape: Shape): Float32Array {
  const out = new Float32Array(shape[0] * shape[1] * shape[2]).fill(NaN);
  for (let v = 0; v < indices.length; v++) out[indices[v]!] = map[v]!;
  return out;
}

/** 1 at the mask's voxels on the dense grid. */
export function maskOf(indices: Uint32Array, shape: Shape): Uint8Array {
  const out = new Uint8Array(shape[0] * shape[1] * shape[2]);
  for (let v = 0; v < indices.length; v++) out[indices[v]!] = 1;
  return out;
}

/** The strongest prediction (the fixture follows the largest value; with a positive one somewhere, that is the strongest positive). */
export function peakOf(map: Float32Array, indices: Uint32Array, grid: Grid): Peak | null {
  if (!map.length) return null;
  let best = 0;
  for (let v = 1; v < map.length; v++) if (map[v]! > map[best]!) best = v;
  const ijk = ijkOf(grid.shape, indices[best]!);
  return { value: map[best]!, ijk, mm: mmOf(grid.affine, ijk) };
}

/** The picture's size for a slice through `axis`: columns × rows. */
export function sliceSize(shape: Shape, axis: Axis): { width: number; height: number } {
  return axis === 2 ? { width: shape[0], height: shape[1] } : axis === 1 ? { width: shape[0], height: shape[2] } : { width: shape[1], height: shape[2] };
}

/** The voxel a picture pixel (column, row) stands for, the fixed axis taken from `at`. Axial: the left of the
 *  picture is the subject's left, anterior up; coronal: left is left, superior up; sagittal: anterior to the left,
 *  superior up. */
export function voxelAt(shape: Shape, axis: Axis, col: number, row: number, at: Ijk): Ijk {
  const out: Ijk = [at[0], at[1], at[2]];
  if (axis === 2) {
    out[0] = col;
    out[1] = shape[1] - 1 - row;
  } else if (axis === 1) {
    out[0] = col;
    out[2] = shape[2] - 1 - row;
  } else {
    out[1] = shape[1] - 1 - col;
    out[2] = shape[2] - 1 - row;
  }
  return out;
}

/** Where voxel `at` falls in a slice through `axis`: column and row of the picture (the crosshair). */
export function pixelOf(shape: Shape, axis: Axis, at: Ijk): { col: number; row: number } {
  if (axis === 2) return { col: at[0], row: shape[1] - 1 - at[1] };
  if (axis === 1) return { col: at[0], row: shape[2] - 1 - at[2] };
  return { col: shape[1] - 1 - at[1], row: shape[2] - 1 - at[2] };
}

/** RGBA pixels of one slice: transparent outside the mask; inside it the change scale over ±`scale`, slate at zero
 *  (and everywhere when there is no map: the cleared view keeps the mask's outline). */
export function slicePixels(volume: Float32Array | null, mask: Uint8Array, shape: Shape, axis: Axis, index: number, scale = SCALE): { width: number; height: number; data: Uint8ClampedArray<ArrayBuffer> } {
  const { width, height } = sliceSize(shape, axis);
  const data = new Uint8ClampedArray(width * height * 4);
  const at: Ijk = [0, 0, 0];
  at[axis] = index;
  for (let row = 0; row < height; row++) {
    for (let col = 0; col < width; col++) {
      const ijk = voxelAt(shape, axis, col, row, at);
      const flat = flatIndex(shape, ijk[0], ijk[1], ijk[2]);
      if (!mask[flat]) continue;
      const value = volume ? volume[flat]! : 0;
      const t = Number.isFinite(value) ? Math.min(1, Math.max(-1, value / scale)) : 0;
      const [r, g, b] = changeColour((t + 1) / 2);
      const p = (row * width + col) * 4;
      data[p] = r;
      data[p + 1] = g;
      data[p + 2] = b;
      data[p + 3] = 255;
    }
  }
  return { width, height, data };
}

/** What the box and the bar carry: one line, trimmed, at most MAX_QUERY characters. */
export const cleanQuery = (text: string): string => text.replace(/[\u0000-\u001f\u007f]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, MAX_QUERY);

export type TextStatus = 'idle' | 'loading' | 'ready' | 'missing' | 'error';

export interface TextState {
  status: TextStatus;
  /** Why the model didn't load, or why the last topic couldn't be encoded. */
  error: string | null;
  header: TextModelHeader | null;
  grid: Grid | null;
  mask: Uint8Array | null;
  /** The submitted topic. */
  query: string;
  /** The terms the model recognised in it, in the encoder's order; none clears the map. */
  terms: string[];
  /** The typed words the model read nothing from (its stop words, words it doesn't know), in the order typed: shown beside
   *  the terms, so “not” in “not pain” is seen to be dropped. A word inside a recognised phrase counts as used. */
  ignored: string[];
  /** The encoder's map over the mask's voxels, null when nothing was recognised. */
  map: Float32Array | null;
  /** The map on the dense grid, NaN outside the mask. */
  volume: Float32Array | null;
  peak: Peak | null;
  /** The voxel the three slices pass through. */
  slice: Ijk;
  /** Start loading the model, once. */
  open(): void;
  /** Take a topic: encoded now when the model is in, otherwise when it arrives. */
  submit(text: string): void;
  setSlice(axis: Axis, index: number): void;
  setPoint(ijk: Ijk): void;
  /** The model, loaded or given (tests). */
  attach(model: TextEncoder): void;
}

let model: TextEncoder | null = null;
let loading: Promise<void> | null = null;

/** The model directory's URL, under the app's sims root. */
export const modelUrl = (): string => `${defaultSource().base}${TEXT_RUN}/`;

/** The typed words the model read nothing from, by its own tokenisation: a word is ignored when leaving it out changes
 *  no term count (stop words, words it doesn't know), so a word inside a recognised phrase counts as used even when the
 *  phrase maps to another term (“visual perception” → vision). NeuroQuery reads terms, not sentences: stop words and word
 *  order are dropped. Shown lower-cased without their punctuation, unique, in the order typed. */
export function ignoredWords(enc: Pick<TextEncoder, 'tokenize'>, text: string): string[] {
  const key = (s: string): string => {
    try {
      return JSON.stringify(enc.tokenize(s).map((x) => [x.index, x.count]));
    } catch {
      return '';
    }
  };
  const words = text.split(/\s+/).filter(Boolean);
  const whole = key(text);
  const out: string[] = [];
  words.forEach((w, i) => {
    const shown = w.toLowerCase().replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');
    if (!shown || out.includes(shown)) return;
    if (key(words.filter((_, j) => j !== i).join(' ')) === whole) out.push(shown);
  });
  return out;
}

function encodeInto(set: (p: Partial<TextState>) => void, get: () => TextState, text: string) {
  if (!model) return;
  const grid = get().grid;
  try {
    const r = model.encode(text);
    const terms = r.terms.map((t) => t.term);
    const ignored = ignoredWords(model, text);
    if (r.map && grid) {
      const peak = peakOf(r.map, model.voxelIndices, grid);
      set({ terms, ignored, map: r.map, volume: denseVolume(r.map, model.voxelIndices, grid.shape), peak, ...(peak ? { slice: peak.ijk } : {}), error: null });
    } else set({ terms, ignored, map: null, volume: null, peak: null, error: null });
  } catch (err) {
    set({ terms: [], ignored: [], map: null, volume: null, peak: null, error: err instanceof Error ? err.message : String(err) });
  }
}

const initial = {
  status: 'idle' as TextStatus,
  error: null,
  header: null,
  grid: null,
  mask: null,
  query: DEFAULT_QUERY,
  terms: [] as string[],
  ignored: [] as string[],
  map: null,
  volume: null,
  peak: null,
  // The fixture's opening slices, until a topic's peak moves them.
  slice: [23, 32, 28] as Ijk,
};

export const useText = create<TextState>((set, get) => ({
  ...initial,
  open() {
    if (model || loading) return;
    set({ status: 'loading', error: null });
    loading = loadTextEncoder(modelUrl())
      .then((m) => get().attach(m))
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : String(err);
        loading = null;
        set({ status: /^Model fetch failed: header\.json/.test(message) ? 'missing' : 'error', error: message });
      });
  },
  attach(m) {
    model = m;
    const shape = m.header.grid.shape;
    const grid: Grid = { shape: [shape[0], shape[1], shape[2]], affine: m.header.grid.affine_ras_mm };
    set({ status: 'ready', error: null, header: m.header, grid, mask: maskOf(m.voxelIndices, grid.shape) });
    encodeInto(set, get, get().query);
  },
  submit(text) {
    const query = cleanQuery(text);
    set({ query });
    encodeInto(set, get, query);
  },
  setSlice(axis, index) {
    const grid = get().grid;
    if (!grid) return;
    const v = Math.min(grid.shape[axis] - 1, Math.max(0, Math.round(index)));
    const slice: Ijk = [...get().slice] as Ijk;
    if (slice[axis] === v) return;
    slice[axis] = v;
    set({ slice });
  },
  setPoint(ijk) {
    const grid = get().grid;
    if (!grid) return;
    set({ slice: [0, 1, 2].map((a) => Math.min(grid.shape[a as Axis] - 1, Math.max(0, Math.round(ijk[a as Axis])))) as Ijk });
  },
}));

/** Forget the model and the view's state (tests). */
export function resetText(): void {
  model = null;
  loading = null;
  useText.setState({ ...initial });
}

if (typeof window !== 'undefined') (window as unknown as { __text?: typeof useText }).__text = useText;
